import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AGENT_GREETINGS,
  AGENT_NAME_STYLES,
  AGENT_TONE_EXAMPLES,
  AGENT_TONE_LABELS,
  AGENT_TONES,
  AI_KEY_STATUS_LABELS,
  PAYMENT_OPTION_LABELS,
  PAYMENT_OPTIONS,
  agentNameText,
  greetingText,
  isBifiOnly,
  openingText,
  type AgentGreeting,
  type AgentNameStyle,
  type AgentTone,
  type AiKeyDTO,
  type ChannelDTO,
  type OrderSettingsDTO,
  type OrderSettingsInput,
  type PaymentOption,
} from '@bifeai/shared';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Select,
  Skeleton,
  Switch,
  useToast,
} from '@bifeai/ui';
import { KeyRound, MessageCircle, Plus, Save, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useProducts } from '../../lib/catalog';
import { api, errorMessage } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { useSession } from '../../lib/session';
import { MercadoPagoCard } from '../settings/MercadoPagoCard';
import './orders-chat.css';

const DAYS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const pesos = (cents: number) => String(Math.round(cents / 100));
const cents = (v: string) => Math.max(0, Math.round(Number(v.replace(/\./g, '').replace(',', '.')) * 100) || 0);

/** Configuración de Pedidos y chat: clave de IA, líneas de WhatsApp y, por sucursal, retiro, envío, pagos y el agente. */
export function OrdersSettingsPage() {
  // Solo bifi no tiene Configuración: Mercado Pago (para el link de pago) se conecta acá.
  const bifiOnly = isBifiOnly(useSession().me.tenant.plan);
  return (
    <>
      <PageHeader
        title="Configurar agente"
        subtitle="La clave de IA, las líneas de WhatsApp y cómo atiende el agente en cada sucursal. La central configura sus locales; cada franquicia, el suyo."
      />
      <SetupGuideCard />
      <AiKeyCard />
      <ChannelsCard />
      {bifiOnly && <MercadoPagoCard paymentLinkOnly />}
      <BranchSettingsCard />
    </>
  );
}

/**
 * Guía para dejar bifi andando: la cuenta de Claude (la inteligencia) y el WhatsApp en Meta. Cada dueño de línea
 * (la marca o una franquicia con su número) hace los dos pasos con sus propias cuentas.
 */
function SetupGuideCard() {
  const { isFranchiseUser } = useSession();
  const who = isFranchiseUser ? 'tu franquicia' : 'la carnicería';
  return (
    <Card>
      <CardHeader
        title="Cómo dejar bifi andando"
        subtitle={`Son dos cuentas a nombre de ${who}: Claude (la inteligencia que responde) y WhatsApp en Meta (el número). Cada una le cobra directo a ${who}; BifeAI no cobra los mensajes.`}
      />
      <CardBody>
        <div style={{ display: 'grid', gap: 10 }}>
          <details className="bf-details">
            <summary>
              <strong>1. Claude: la clave de IA</strong> · unos 10 minutos
            </summary>
            <ol className="bf-hw-steps">
              <li>
                Entrá a <strong>console.anthropic.com</strong> y creá una cuenta con el email de {who} (no uses una cuenta personal: la clave queda
                para el negocio).
              </li>
              <li>
                En <strong>Settings → Billing</strong> cargá una tarjeta y comprá crédito. Es prepago: Anthropic descuenta lo que usa bifi. Conviene
                prender la recarga automática para que no se quede sin crédito un sábado.
              </li>
              <li>
                En <strong>API Keys</strong> tocá <strong>Create Key</strong>, ponele de nombre «BifeAI» y copiala (empieza con <code>sk-ant-</code>).
                Anthropic la muestra una sola vez.
              </li>
              <li>
                Pegala acá abajo, en <strong>Inteligencia artificial</strong>, y tocá <strong>Probar y guardar</strong>. Tiene que quedar en
                «Funcionando».
              </li>
              <li>
                Si la clave se queda sin crédito o deja de andar, bifi no contesta y los mensajes quedan en el chat para que responda una persona. Lo
                ves acá con el aviso en rojo. Si alguien vio la clave, borrala en API Keys y cargá una nueva.
              </li>
            </ol>
          </details>
          <details className="bf-details">
            <summary>
              <strong>2. WhatsApp: conectar el número en Meta</strong> · unos 15 minutos
            </summary>
            <ol className="bf-hw-steps">
              <li>
                Tené a mano: un usuario de Facebook que vaya a administrar la cuenta de empresa, y el celular del número (llega un código por SMS o
                llamada).
              </li>
              <li>
                En <strong>Líneas de WhatsApp</strong> tocá <strong>Conectar WhatsApp</strong>. Se abre la ventana de Meta: elegí o creá el portfolio
                comercial de {who}, la cuenta de WhatsApp Business y el número, y verificalo con el código.
              </li>
              <li>
                Si el número ya usa la app <strong>WhatsApp Business</strong>, elegí la opción para seguir usándola: el celular sigue funcionando y
                bifi contesta al mismo tiempo.
              </li>
              <li>
                Al terminar, cargá una tarjeta en Meta (<strong>WhatsApp Manager → Facturación</strong>). Meta le cobra a {who} los mensajes que
                correspondan; cada número tiene 1.000 respuestas gratis por mes.
              </li>
              <li>Volvé acá y elegí qué sucursales atiende el número. El aviso de «pedido listo» se crea solo; Meta lo revisa en unos minutos.</li>
              <li>
                bifi se paga por número conectado: antes de conectar uno nuevo te mostramos cuánto suma. Si lo desconectás, deja de cobrarse desde el
                mes siguiente.
              </li>
            </ol>
          </details>
          <details className="bf-details">
            <summary>
              <strong>Franquicias</strong>
            </summary>
            <ul className="bf-hw-steps">
              {isFranchiseUser ? (
                <>
                  <li>
                    Para usar bifi, tu franquicia conecta su propio número con sus propias cuentas: tu clave de Claude y tu WhatsApp en Meta (los dos
                    pasos de arriba, desde tu usuario).
                  </li>
                  <li>Si la marca eligió que cada franquiciado pague su bifi, primero activá tu abono en Mi plan.</li>
                  <li>Tus chats, pedidos y clientes son solo tuyos: la casa central no los ve.</li>
                </>
              ) : (
                <>
                  <li>
                    Cada franquicia que quiera bifi conecta su propio número desde su usuario, con su propia clave de Claude y su cuenta de Meta. Los
                    números de la marca atienden solo a los locales propios.
                  </li>
                  <li>Quién paga el bifi de cada franquicia lo elegís en Mi plan: la marca o cada franquiciado.</li>
                  <li>Los chats, pedidos y clientes de cada franquicia son solo suyos: la casa central no los ve.</li>
                </>
              )}
            </ul>
          </details>
        </div>
      </CardBody>
    </Card>
  );
}

function AiKeyCard() {
  const qc = useQueryClient();
  const toast = useToast();
  const ai = useQuery({ queryKey: ['orders-chat', 'ai'], queryFn: () => api.get<AiKeyDTO>('/orders-chat/ai') });
  const [key, setKey] = useState('');
  const done = (d: AiKeyDTO) => qc.setQueryData(['orders-chat', 'ai'], d);
  const save = useMutation({
    mutationFn: () => api.put<AiKeyDTO>('/orders-chat/ai/key', { apiKey: key }),
    onSuccess: (d) => {
      setKey('');
      done(d);
      if (d.status === 'OK') toast.success('Clave guardada y probada', 'El agente ya puede responder.');
      else toast.error('Clave guardada, pero con un problema', AI_KEY_STATUS_LABELS[d.status] ?? d.status);
    },
    onError: (err) => toast.error('No se guardó la clave', errorMessage(err)),
  });
  const pause = useMutation({
    mutationFn: (paused: boolean) => api.put<AiKeyDTO>('/orders-chat/ai/paused', { paused }),
    onSuccess: (d) => {
      done(d);
      toast.success(d.paused ? 'Agente pausado: los mensajes quedan para que conteste una persona' : 'Agente activo');
    },
    onError: (err) => toast.error('No se pudo cambiar', errorMessage(err)),
  });
  const d = ai.data;
  return (
    <Card>
      <CardHeader
        title="Inteligencia artificial"
        subtitle="El agente usa Claude, de Anthropic. La cuenta es de la carnicería y Anthropic le cobra directo por lo que use; BifeAI no cobra los mensajes."
      />
      <CardBody>
        {!d ? (
          <Skeleton />
        ) : (
          <div className="bf-form">
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <Badge tone={d.status === 'OK' ? 'success' : d.hasKey ? 'danger' : 'neutral'} dot>
                {d.hasKey ? (AI_KEY_STATUS_LABELS[d.status] ?? d.status) : 'Sin clave cargada'}
              </Badge>
              {d.keyHint && <Badge>Clave terminada en …{d.keyHint}</Badge>}
              {d.lastCheckedAt && <span className="bf-cell-sub">Última prueba: {formatDateTime(d.lastCheckedAt)}</span>}
            </div>
            {d.lastError && <Callout tone="danger">{d.lastError}</Callout>}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (key.trim()) save.mutate();
              }}
              style={{ display: 'flex', gap: 8, alignItems: 'end', flexWrap: 'wrap' }}
            >
              <div style={{ flex: '1 1 320px' }}>
                <Field
                  label={d.hasKey ? 'Cambiar la clave' : 'Clave de la API de Anthropic'}
                  hint="Se crea en console.anthropic.com → API Keys. Se guarda cifrada y nunca se vuelve a mostrar."
                >
                  <Input type="password" autoComplete="off" value={key} onChange={(e) => setKey(e.target.value)} placeholder="sk-ant-…" />
                </Field>
              </div>
              <Button type="submit" icon={<KeyRound />} loading={save.isPending} disabled={!key.trim()}>
                Probar y guardar
              </Button>
            </form>
            <label style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <Switch checked={!d.paused} onCheckedChange={(v) => pause.mutate(!v)} disabled={pause.isPending} label="Agente activo" />
              {d.paused ? 'Agente pausado: todo queda para que conteste una persona' : 'Agente activo'}
            </label>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function ChannelsCard() {
  const qc = useQueryClient();
  const toast = useToast();
  const { me } = useSession();
  const channels = useQuery({ queryKey: ['orders-chat', 'channels'], queryFn: () => api.get<ChannelDTO[]>('/orders-chat/channels') });
  const settings = useQuery({ queryKey: ['orders-chat', 'settings'], queryFn: () => api.get<OrderSettingsDTO[]>('/orders-chat/settings') });
  const save = useMutation({
    mutationFn: ({ id, branchIds }: { id: string; branchIds: string[] }) =>
      api.put<ChannelDTO[]>(`/orders-chat/channels/${id}/branches`, { branchIds }),
    onSuccess: (d) => {
      qc.setQueryData(['orders-chat', 'channels'], d);
      toast.success('Sucursales de la línea guardadas');
    },
    onError: (err) => toast.error('No se pudo guardar', errorMessage(err)),
  });
  const template = useMutation({
    mutationFn: (id: string) => api.post<ChannelDTO[]>(`/orders-chat/channels/${id}/ready-template`),
    onSuccess: (d) => qc.setQueryData(['orders-chat', 'channels'], d),
    onError: (err) => toast.error('No se pudo consultar a WhatsApp', errorMessage(err)),
  });
  const disconnect = useMutation({
    mutationFn: (id: string) => api.del<ChannelDTO[]>(`/orders-chat/channels/${id}`),
    onSuccess: async (d) => {
      qc.setQueryData(['orders-chat', 'channels'], d);
      toast.success('Línea desconectada', 'bifi deja de cobrarla desde el mes que viene.');
      await qc.invalidateQueries({ queryKey: ['my-plan'] });
    },
    onError: (err) => toast.error('No se pudo desconectar', errorMessage(err)),
  });
  const wholesaleLine = useMutation({
    mutationFn: ({ id, wholesale }: { id: string; wholesale: boolean }) =>
      api.put<ChannelDTO[]>(`/orders-chat/channels/${id}/wholesale`, { wholesale }),
    onSuccess: (d, v) => {
      qc.setQueryData(['orders-chat', 'channels'], d);
      toast.success(v.wholesale ? 'Línea solo para mayoristas' : 'La línea atiende como siempre');
    },
    onError: (err) => toast.error('No se pudo guardar', errorMessage(err)),
  });
  const hasWholesale = me.tenant.modules.includes('WHOLESALE');
  const branches = settings.data ?? [];
  // El registro de Meta se abre en su propia página, con carga completa (tiene otra política de seguridad).
  const connect = (
    <Button size="sm" icon={<MessageCircle />} onClick={() => window.location.assign('/conectar-whatsapp')}>
      Conectar WhatsApp
    </Button>
  );
  return (
    <Card>
      <CardHeader
        title="Líneas de WhatsApp"
        subtitle="Cada número conectado y qué sucursales atiende. Una línea puede atender una sucursal o varias (el agente pregunta por cuál retira). Meta le cobra directo a la carnicería."
        actions={connect}
      />
      <CardBody>
        {channels.isPending ? (
          <Skeleton />
        ) : !channels.data?.length ? (
          <EmptyState
            icon={<MessageCircle />}
            title="Todavía no hay líneas conectadas"
            text="Tocá Conectar WhatsApp: se abre una ventana de Facebook para elegir la cuenta de empresa y verificar el número."
          />
        ) : (
          <div style={{ display: 'grid', gap: 16 }}>
            {channels.data.map((c) => {
              const own = branches.filter((b) => (c.ownerBranchId ? b.branchId === c.ownerBranchId : !b.franchise));
              const editable = c.ownerBranchId ? me.membership.branchId === c.ownerBranchId : !me.membership.branchId;
              return (
                <div
                  key={c.id}
                  style={{ border: '1px solid var(--bf-line)', borderRadius: 'var(--bf-radius-md)', padding: 14, display: 'grid', gap: 10 }}
                >
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                    <div>
                      <strong>{c.name}</strong>
                      <div className="bf-cell-sub">
                        {c.displayPhone ?? 'Número sin confirmar'}
                        {c.displayName ? ` · ${c.displayName}` : ''}
                      </div>
                    </div>
                    <Badge tone={c.status === 'ACTIVE' ? 'success' : c.status === 'ERROR' ? 'danger' : 'warning'} dot>
                      {c.status === 'ACTIVE' ? 'Conectada' : c.status === 'ERROR' ? 'Con error' : 'Pendiente'}
                    </Badge>
                  </div>
                  {c.lastError && <Callout tone="danger">{c.lastError}</Callout>}
                  <TemplateStatus channel={c} busy={template.isPending} onSync={() => template.mutate(c.id)} />
                  <div className="bf-cell-sub">
                    Respuestas del mes: {c.monthCount.toLocaleString('es-AR')} de {c.freeTier.toLocaleString('es-AR')} gratis
                    {c.monthCount >= c.freeTier * 0.8 ? ' (desde la 1.001 Meta cobra cada mensaje)' : ''}
                  </div>
                  {editable && (
                    <div>
                      <Button
                        size="sm"
                        variant="ghost"
                        loading={disconnect.isPending && disconnect.variables === c.id}
                        onClick={() => {
                          if (
                            window.confirm(
                              `¿Desconectar ${c.displayPhone ?? c.name} de BifeAI? bifi deja de contestar en ese número y deja de cobrarse desde el mes que viene. Las conversaciones quedan guardadas y el número sigue siendo tuyo en WhatsApp.`,
                            )
                          )
                            disconnect.mutate(c.id);
                        }}
                      >
                        Desconectar
                      </Button>
                    </div>
                  )}
                  {hasWholesale && !c.ownerBranchId && (
                    <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <Switch
                        checked={c.wholesale}
                        disabled={!editable || wholesaleLine.isPending}
                        onCheckedChange={(v) => wholesaleLine.mutate({ id: c.id, wholesale: v })}
                        label="Solo mayoristas"
                      />
                      <span>
                        Solo mayoristas <span className="bf-cell-sub">(bifi atiende a todos como clientes mayoristas, con su lista)</span>
                      </span>
                    </label>
                  )}
                  <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                    {own.map((b) => (
                      <label key={b.branchId} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <Switch
                          checked={c.branchIds.includes(b.branchId)}
                          disabled={!editable || save.isPending}
                          onCheckedChange={(v) =>
                            save.mutate({ id: c.id, branchIds: v ? [...c.branchIds, b.branchId] : c.branchIds.filter((x) => x !== b.branchId) })
                          }
                          label={b.branchName}
                        />
                        {b.branchName}
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

/** Estado de la plantilla "pedido listo" (para avisar fuera de las 24 h) en la cuenta de WhatsApp de la línea. */
function TemplateStatus({ channel: c, busy, onSync }: { channel: ChannelDTO; busy: boolean; onSync: () => void }) {
  const s = c.readyTemplateStatus;
  const label =
    s === 'APPROVED'
      ? 'Aprobado por Meta'
      : s === 'PENDING'
        ? 'En revisión de Meta'
        : s === 'REJECTED'
          ? 'Rechazado por Meta'
          : s === 'ERROR'
            ? 'No se pudo crear'
            : 'Sin activar';
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}>
      <div>
        <div>
          Aviso de pedido listo:{' '}
          <Badge tone={s === 'APPROVED' ? 'success' : s === 'PENDING' || !s ? 'warning' : 'danger'} dot>
            {label}
          </Badge>
        </div>
        <div className="bf-cell-sub">
          {s === 'APPROVED'
            ? 'Le avisás al cliente desde Pedidos aunque no te haya escrito en las últimas 24 horas. Meta cobra cada aviso a la cuenta de la carnicería.'
            : s === 'PENDING'
              ? 'Meta la revisa sola: suele tardar unos minutos. Mientras tanto, el aviso se manda desde WhatsApp Web.'
              : (c.readyTemplateError ??
                'Permite avisar desde Pedidos aunque el cliente no haya escrito en las últimas 24 horas. Para usarla, la cuenta de WhatsApp tiene que tener un medio de pago cargado en Meta.')}
        </div>
      </div>
      {s !== 'APPROVED' && (
        <Button size="sm" loading={busy} onClick={onSync}>
          {s ? 'Actualizar estado' : 'Activar aviso'}
        </Button>
      )}
    </div>
  );
}

type Form = Omit<OrderSettingsInput, 'minOrderCents' | 'deliveryZones'> & { minOrder: string; zones: { name: string; fee: string; min: string }[] };

// Valores viejos (texto libre) o desconocidos vuelven a la opción por defecto.
const nameStyleOf = (v: string | null | undefined): AgentNameStyle =>
  (AGENT_NAME_STYLES as readonly string[]).includes(v ?? '') ? (v as AgentNameStyle) : 'BIFI';
const greetingOf = (v: string | null | undefined): AgentGreeting =>
  (AGENT_GREETINGS as readonly string[]).includes(v ?? '') ? (v as AgentGreeting) : 'NINGUNO';

type Hours = OrderSettingsInput['hours'];
type DayRow = { open: boolean; a1: string; a2: string; b1: string; b2: string };
/** Lunes primero, como se piensa la semana en el mostrador. */
const WEEK = [1, 2, 3, 4, 5, 6, 0];

function toRows(hours: Hours): DayRow[] {
  return DAYS.map((_, day) => {
    const r = hours.filter((h) => h.day === day).sort((x, y) => x.from.localeCompare(y.from));
    return { open: r.length > 0, a1: r[0]?.from ?? '', a2: r[0]?.to ?? '', b1: r[1]?.from ?? '', b2: r[1]?.to ?? '' };
  });
}
const okRange = (from: string, to: string) => Boolean(from && to && from < to);
function toHours(rows: DayRow[]): Hours {
  return rows.flatMap((r, day) =>
    r.open ? [...(okRange(r.a1, r.a2) ? [{ day, from: r.a1, to: r.a2 }] : []), ...(okRange(r.b1, r.b2) ? [{ day, from: r.b1, to: r.b2 }] : [])] : [],
  );
}

/**
 * Horarios por día con dos turnos (abre, cierra, abre, cierra), porque la mayoría de las carnicerías cierra al
 * mediodía. El turno de la tarde es opcional: vacío es horario corrido.
 */
function HoursEditor({ value, onChange }: { value: Hours; onChange: (h: Hours) => void }) {
  const [rows, setRows] = useState(() => toRows(value));
  const update = (next: DayRow[]) => {
    setRows(next);
    onChange(toHours(next));
  };
  const edit = (day: number, patch: Partial<DayRow>) => update(rows.map((r, d) => (d === day ? { ...r, ...patch } : r)));
  const preset = () =>
    update(
      DAYS.map((_, day) =>
        day === 0 ? { open: false, a1: '', a2: '', b1: '', b2: '' } : { open: true, a1: '09:00', a2: '13:00', b1: '17:00', b2: '21:00' },
      ),
    );
  const copyMonday = () => update(rows.map((r, day) => (day === 0 || day === 1 ? r : { ...rows[1]! })));
  const time = (day: number, k: 'a1' | 'a2' | 'b1' | 'b2', label: string) => (
    <Input type="time" value={rows[day]![k]} onChange={(e) => edit(day, { [k]: e.target.value })} aria-label={`${DAYS[day]}: ${label}`} />
  );

  return (
    <div className="bf-hours">
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Button size="sm" variant="secondary" onClick={preset}>
          Lunes a sábado de 9 a 13 y de 17 a 21
        </Button>
        <Button size="sm" variant="ghost" onClick={copyMonday} disabled={!rows[1]!.open}>
          Copiar el lunes a martes–sábado
        </Button>
      </div>
      {value.length === 0 && <p className="bf-cell-sub">Sin horarios: el agente pregunta el horario y aclara que lo confirma la carnicería.</p>}
      {WEEK.map((day) => {
        const r = rows[day]!;
        const bad = r.open && (((r.a1 || r.a2) && !okRange(r.a1, r.a2)) || ((r.b1 || r.b2) && !okRange(r.b1, r.b2)));
        return (
          <div key={day} className="bf-hours-row">
            <label className="bf-hours-day">
              <Switch
                checked={r.open}
                onCheckedChange={(v) => edit(day, v && !r.a1 ? { open: true, a1: '09:00', a2: '13:00' } : { open: v })}
                label={`${DAYS[day]} abre`}
              />
              {DAYS[day]}
            </label>
            {r.open ? (
              <div className="bf-hours-times">
                <span className="bf-hours-shift">
                  <span className="bf-cell-sub">Mañana</span>
                  {time(day, 'a1', 'abre a la mañana')}
                  <span aria-hidden>a</span>
                  {time(day, 'a2', 'cierra al mediodía')}
                </span>
                <span className="bf-hours-shift">
                  <span className="bf-cell-sub">Tarde</span>
                  {time(day, 'b1', 'abre a la tarde')}
                  <span aria-hidden>a</span>
                  {time(day, 'b2', 'cierra a la noche')}
                </span>
              </div>
            ) : (
              <span className="bf-cell-sub">Cerrado</span>
            )}
            {bad && (
              <span className="bf-cell-sub" style={{ color: 'var(--bf-danger)' }}>
                Revisá los horarios: cada turno cierra después de abrir.
              </span>
            )}
          </div>
        );
      })}
      <p className="bf-cell-sub">Si no cierra al mediodía, poné el horario corrido en Mañana y dejá Tarde vacío.</p>
    </div>
  );
}

function toForm(s: OrderSettingsDTO): Form {
  return {
    pickupEnabled: s.pickupEnabled,
    deliveryEnabled: s.deliveryEnabled,
    pickupPayments: s.pickupPayments,
    deliveryPayments: s.deliveryPayments,
    hours: s.hours,
    prepMinutes: s.prepMinutes,
    agentName: nameStyleOf(s.agentName),
    agentTone: s.agentTone,
    greeting: greetingOf(s.greeting),
    excludedProductIds: s.excludedProductIds,
    autoNotices: s.autoNotices,
    agentSendsPaymentLink: s.agentSendsPaymentLink,
    agentCrossSell: s.agentCrossSell,
    minOrder: s.minOrderCents ? pesos(s.minOrderCents) : '',
    zones: s.deliveryZones.map((z) => ({ name: z.name, fee: pesos(z.feeCents), min: z.minOrderCents ? pesos(z.minOrderCents) : '' })),
  };
}

function BranchSettingsCard() {
  const qc = useQueryClient();
  const business = useSession().me.tenant.name;
  const toast = useToast();
  const settings = useQuery({ queryKey: ['orders-chat', 'settings'], queryFn: () => api.get<OrderSettingsDTO[]>('/orders-chat/settings') });
  const editable = useMemo(() => (settings.data ?? []).filter((s) => s.editable), [settings.data]);
  const [branchId, setBranchId] = useState('');
  const current = editable.find((s) => s.branchId === branchId) ?? editable[0];
  const [f, setF] = useState<Form | null>(null);
  const [productSearch, setProductSearch] = useState('');
  const products = useProducts({ search: productSearch || undefined });
  useEffect(() => {
    if (current) setF(toForm(current));
  }, [current]);

  const save = useMutation({
    mutationFn: (body: OrderSettingsInput) => api.put<OrderSettingsDTO[]>(`/orders-chat/settings/${current!.branchId}`, body),
    onSuccess: (d) => {
      qc.setQueryData(['orders-chat', 'settings'], d);
      toast.success(`Configuración de ${current!.branchName} guardada`, 'El agente la usa desde el próximo mensaje.');
    },
    onError: (err) => toast.error('No se pudo guardar', errorMessage(err)),
  });

  if (settings.isPending) return <Skeleton style={{ height: 300 }} />;
  if (!current || !f) {
    return <Callout>No tenés sucursales para configurar desde esta sesión. Las franquicias configuran la suya.</Callout>;
  }
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setF({ ...f, [k]: v });
  const nameStyle = nameStyleOf(f.agentName);
  const greetingKey = greetingOf(f.greeting);
  const togglePay = (k: 'pickupPayments' | 'deliveryPayments', p: PaymentOption) =>
    set(k, f[k].includes(p) ? f[k].filter((x) => x !== p) : [...f[k], p]);
  const submit = () =>
    save.mutate({
      pickupEnabled: f.pickupEnabled,
      deliveryEnabled: f.deliveryEnabled,
      pickupPayments: f.pickupPayments,
      deliveryPayments: f.deliveryPayments,
      deliveryZones: f.zones
        .filter((z) => z.name.trim())
        .map((z) => ({ name: z.name.trim(), feeCents: cents(z.fee), minOrderCents: z.min ? cents(z.min) : null })),
      minOrderCents: f.minOrder ? cents(f.minOrder) : 0,
      hours: f.hours,
      prepMinutes: f.prepMinutes,
      agentName: nameStyleOf(f.agentName),
      agentTone: f.agentTone,
      greeting: greetingOf(f.greeting),
      excludedProductIds: f.excludedProductIds,
      autoNotices: f.autoNotices,
      agentSendsPaymentLink: f.agentSendsPaymentLink,
      agentCrossSell: f.agentCrossSell,
    });

  return (
    <Card>
      <CardHeader
        title="Pedidos y agente por sucursal"
        subtitle="Retiro, envío, pagos, horarios y cómo se presenta el agente."
        actions={
          editable.length > 1 && (
            <Select value={current.branchId} onChange={(e) => setBranchId(e.target.value)} aria-label="Sucursal">
              {editable.map((s) => (
                <option key={s.branchId} value={s.branchId}>
                  {s.branchName}
                </option>
              ))}
            </Select>
          )
        }
      />
      <CardBody>
        <div className="bf-form">
          <p className="bf-label">Retiro en el local</p>
          <label style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <Switch checked={f.pickupEnabled} onCheckedChange={(v) => set('pickupEnabled', v)} label="Retiro en el local" />
            Los clientes pueden retirar en el local
          </label>
          {f.pickupEnabled && (
            <div className="bf-chips" aria-label="Pagos para retiro">
              {PAYMENT_OPTIONS.map((p) => (
                <button
                  key={p}
                  type="button"
                  className="bf-chip"
                  aria-pressed={f.pickupPayments.includes(p)}
                  onClick={() => togglePay('pickupPayments', p)}
                >
                  {PAYMENT_OPTION_LABELS[p]}
                </button>
              ))}
            </div>
          )}

          <p className="bf-label">Envío a domicilio</p>
          <label style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <Switch checked={f.deliveryEnabled} onCheckedChange={(v) => set('deliveryEnabled', v)} label="Envío a domicilio" />
            Hacemos envíos
          </label>
          {f.deliveryEnabled && (
            <>
              <div className="bf-chips" aria-label="Pagos para envío">
                {PAYMENT_OPTIONS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    className="bf-chip"
                    aria-pressed={f.deliveryPayments.includes(p)}
                    onClick={() => togglePay('deliveryPayments', p)}
                  >
                    {PAYMENT_OPTION_LABELS[p]}
                  </button>
                ))}
              </div>
              <div style={{ display: 'grid', gap: 8 }}>
                {f.zones.map((z, i) => (
                  <div key={i} className="bf-form-row" style={{ alignItems: 'end' }}>
                    <Field label="Zona">
                      <Input
                        value={z.name}
                        onChange={(e) =>
                          set(
                            'zones',
                            f.zones.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)),
                          )
                        }
                        placeholder="Ej.: Centro"
                      />
                    </Field>
                    <Field label="Costo de envío ($)">
                      <Input
                        inputMode="numeric"
                        value={z.fee}
                        onChange={(e) =>
                          set(
                            'zones',
                            f.zones.map((x, j) => (j === i ? { ...x, fee: e.target.value } : x)),
                          )
                        }
                      />
                    </Field>
                    <Field label="Mínimo de la zona ($)" hint="Opcional">
                      <Input
                        inputMode="numeric"
                        value={z.min}
                        onChange={(e) =>
                          set(
                            'zones',
                            f.zones.map((x, j) => (j === i ? { ...x, min: e.target.value } : x)),
                          )
                        }
                      />
                    </Field>
                    <Button
                      variant="ghost"
                      iconOnly
                      icon={<Trash2 />}
                      aria-label="Quitar zona"
                      onClick={() =>
                        set(
                          'zones',
                          f.zones.filter((_, j) => j !== i),
                        )
                      }
                    >
                      Quitar zona
                    </Button>
                  </div>
                ))}
                <div>
                  <Button size="sm" variant="secondary" icon={<Plus />} onClick={() => set('zones', [...f.zones, { name: '', fee: '', min: '' }])}>
                    Agregar zona
                  </Button>
                </div>
              </div>
            </>
          )}

          <div className="bf-form-row">
            <Field label="Pedido mínimo para envío ($)" hint="Vacío: sin mínimo. Para retirar en el local no hay mínimo.">
              <Input inputMode="numeric" value={f.minOrder} onChange={(e) => set('minOrder', e.target.value)} />
            </Field>
            <Field label="Anticipación mínima (minutos)" hint="Tiempo para preparar un pedido">
              <Input
                inputMode="numeric"
                value={String(f.prepMinutes)}
                onChange={(e) => set('prepMinutes', Math.max(0, Number(e.target.value) || 0))}
              />
            </Field>
          </div>

          <p className="bf-label">Horarios de atención</p>
          <HoursEditor key={current.branchId} value={f.hours} onChange={(h) => set('hours', h)} />

          <p className="bf-label">El agente</p>
          <div className="bf-form-row">
            <Field label="Cómo se presenta" hint="Quién atiende. Por defecto, bifi, el asistente de ventas de BifeAI.">
              <Select value={nameStyle} onChange={(e) => set('agentName', nameStyleOf(e.target.value))}>
                {AGENT_NAME_STYLES.map((k) => (
                  <option key={k} value={k}>
                    Soy {agentNameText(k, business)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Tono" hint={`Ejemplo: “${AGENT_TONE_EXAMPLES[f.agentTone]}”`}>
              <Select value={f.agentTone} onChange={(e) => set('agentTone', e.target.value as AgentTone)}>
                {AGENT_TONES.map((t) => (
                  <option key={t} value={t}>
                    {AGENT_TONE_LABELS[t]}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <Field label="Saludo del primer mensaje" hint={`Así arranca el primer mensaje: “${openingText(nameStyle, greetingKey, business)}”`}>
            <Select value={greetingKey} onChange={(e) => set('greeting', greetingOf(e.target.value))}>
              {AGENT_GREETINGS.map((k) => (
                <option key={k} value={k}>
                  {greetingText(k, business) ?? 'Solo “¡Hola!”'}
                </option>
              ))}
            </Select>
          </Field>
          <label style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <Switch checked={f.autoNotices} onCheckedChange={(v) => set('autoNotices', v)} label="Avisos automáticos" />
            Avisarle al cliente cuando el pedido está listo (cada aviso puede tener costo en Meta)
          </label>
          <label style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <Switch checked={f.agentSendsPaymentLink} onCheckedChange={(v) => set('agentSendsPaymentLink', v)} label="Link de pago del agente" />
            Si todo el pedido es de precio fijo, el agente manda el link de Mercado Pago
          </label>
          <label style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <Switch checked={f.agentCrossSell} onCheckedChange={(v) => set('agentCrossSell', v)} label="Venta cruzada" />
            Antes de cerrar el pedido, el agente ofrece una sola cosa que lo acompañe, según el tipo de cliente y lo que suele llevar
          </label>

          <p className="bf-label">Productos que el agente no ofrece</p>
          <p className="bf-cell-sub">Alcohol y tabaco nunca se ofrecen por WhatsApp. Marcá además lo que no quieras vender por este medio.</p>
          <Input
            value={productSearch}
            onChange={(e) => setProductSearch(e.target.value)}
            placeholder="Buscar producto"
            aria-label="Buscar producto"
          />
          <div
            style={{
              maxHeight: 220,
              overflow: 'auto',
              border: '1px solid var(--bf-line)',
              borderRadius: 'var(--bf-radius-md)',
              padding: 8,
              display: 'grid',
              gap: 6,
            }}
          >
            {(products.data ?? [])
              .filter((p) => p.sellable)
              .slice(0, 80)
              .map((p) => (
                <label key={p.id} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    checked={f.excludedProductIds.includes(p.id)}
                    onChange={(e) =>
                      set('excludedProductIds', e.target.checked ? [...f.excludedProductIds, p.id] : f.excludedProductIds.filter((x) => x !== p.id))
                    }
                  />
                  {p.name}
                </label>
              ))}
          </div>
          {f.excludedProductIds.length > 0 && <p className="bf-cell-sub">{f.excludedProductIds.length} producto(s) no se ofrecen.</p>}

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button icon={<Save />} loading={save.isPending} onClick={submit}>
              Guardar
            </Button>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}
